import React, { useState, useEffect, useMemo, useRef } from "react";
import { Form, Select, Spin, Empty, Switch, Button, message, Table, Tag, Popconfirm } from "antd";
import {
    GlobalOutlined, AppstoreOutlined, ShopOutlined,
    SaveOutlined, DeleteOutlined, SyncOutlined, CheckCircleFilled, CloseOutlined
} from "@ant-design/icons";
import ReturnRuleServ from "../../service/ReturnRuleServ";
import CategoryServ from "../../service/CategoryServ";
import ShopServ from "../../service/ShopServ";
import Breadcrumbs from "../../component/Breadcrumbs";

const { Option } = Select;

const TYPE_META = {
    global: { icon: <GlobalOutlined />, label: "Global Level", color: "#FF8110", tagColor: "#c2410c", tagBg: "#fff7ed" },
    category: { icon: <AppstoreOutlined />, label: "Category Level", color: "#3b82f6", tagColor: "#1d4ed8", tagBg: "#eff6ff" },
    shop: { icon: <ShopOutlined />, label: "Shop Level", color: "#8b5cf6", tagColor: "#6d28d9", tagBg: "#f5f3ff" },
};
const PRIORITY_LABEL = { shop: "High", category: "Medium", global: "Standard" };
const PRIORITY_COLOR = { shop: "#8b5cf6", category: "#3b82f6", global: "#FF8110" };

const card = {
    background: "rgba(255,255,255,0.82)",
    backdropFilter: "blur(18px)",
    WebkitBackdropFilter: "blur(18px)",
    borderRadius: 20,
    border: "1.5px solid rgba(255,129,16,0.12)",
    boxShadow: "0 8px 32px rgba(41,5,101,0.08)",
    overflow: "hidden",
    marginBottom: 24,
};

const cardHeaderPurple = {
    background: "linear-gradient(135deg, #0B1F4D 0%, #4A1A8A 100%)",
    padding: "18px 24px",
    display: "flex", alignItems: "center", gap: 12,
};
const cardHeaderGreen = {
    background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
    padding: "18px 24px",
    display: "flex", alignItems: "center", gap: 12,
};
const headerIcon = {
    width: 38, height: 38, borderRadius: 11,
    background: "rgba(255,255,255,0.15)",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 17, color: "#FF8110",
};
const headerTitle = { margin: 0, color: "#fff", fontSize: 16, fontWeight: 700 };
const headerSub = { margin: "2px 0 0", color: "rgba(255,255,255,0.6)", fontSize: 12 };

const label = { fontSize: 13, fontWeight: 600, color: "#0B1F4D", marginBottom: 8, display: "block" };
const req = { color: "#FF8110", marginRight: 3 };

/* ── table ── */
const thCell = {
    padding: "11px 16px", fontSize: 11, fontWeight: 700, color: "#0B1F4D",
    textTransform: "uppercase", letterSpacing: 0.7,
    borderBottom: "1.5px solid #ede9f6", whiteSpace: "nowrap", textAlign: "left",
};
const tdCell = {
    padding: "13px 16px", fontSize: 13, color: "#1a1a2e",
    borderBottom: "1px solid #f4f0fb", verticalAlign: "middle",
};

const pill = (color, bg) => ({
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "3px 10px", borderRadius: 20, fontSize: 10,
    fontWeight: 700, color, background: bg,
    letterSpacing: 0.5, textTransform: "uppercase",
});

const ReturnControl = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const [hoveredRow, setHoveredRow] = useState(null);
    const [editingRule, setEditingRule] = useState(null);
    const formCardRef = useRef(null);

    const [categories, setCategories] = useState([]);
    const [parentCategories, setParentCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [shops, setShops] = useState([]);
    const [allRules, setAllRules] = useState([]);

    const [targetType, setTargetType] = useState("global");
    const [selectedParentId, setSelectedParentId] = useState(null);
    const [selectedSubId, setSelectedSubId] = useState(null);
    const [selectedShopId, setSelectedShopId] = useState(null);

    const allowReturn = Form.useWatch("allowReturn", form) ?? false;

    useEffect(() => {
        (async () => {
            setPageLoading(true);
            try {
                const [catRes, shopRes, ruleRes] = await Promise.all([
                    CategoryServ.index({ limit: 1000 }),
                    ShopServ.adminIndex({ limit: 1000 }),
                    ReturnRuleServ.getAllRules()
                ]);

                const extractArray = (res, key) => Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : (Array.isArray(res?.data?.[key]) ? res.data[key] : []));

                const cats = extractArray(catRes, 'categories');
                setCategories(cats);
                setParentCategories(cats.filter(c => !c.parentId));
                
                const shopList = extractArray(shopRes, 'shops');
                setShops(shopList);
                
                const ruleList = extractArray(ruleRes, 'data');
                setAllRules(ruleList);
            } catch (error) {
                console.error(error);
                message.error("Failed to load data");
            } finally {
                setPageLoading(false);
            }
        })();
    }, []);

    useEffect(() => {
        let rule = null;
        if (targetType === "global") rule = allRules.find(r => r.type === "global");
        else if (targetType === "category") {
            const id = selectedSubId || selectedParentId;
            if (id) rule = allRules.find(r => r.type === "category" && r.categoryId?._id === id);
        } else if (targetType === "shop" && selectedShopId) {
            rule = allRules.find(r => r.type === "shop" && r.shopId?._id === selectedShopId);
        }
        form.setFieldsValue({ allowReturn: rule ? rule.allowReturn : false });
    }, [targetType, selectedParentId, selectedSubId, selectedShopId, allRules, form]);

    const onParentChange = val => {
        setSelectedParentId(val); setSelectedSubId(null);
        setSubCategories(val ? categories.filter(c => c.parentId === val) : []);
    };

    const handleSave = async (values) => {
        if (targetType === "category" && !(selectedSubId || selectedParentId)) { message.error("Please select a category"); return; }
        if (targetType === "shop" && !selectedShopId) { message.error("Please select a shop"); return; }
        
        try {
            setLoading(true);
            const payload = {
                type: targetType,
                allowReturn: values.allowReturn,
                categoryId: targetType === "category" ? (selectedSubId || selectedParentId) : null,
                shopId: targetType === "shop" ? selectedShopId : null,
            };

            await ReturnRuleServ.upsertRule(payload);
            message.success("Return rule saved successfully");
            setEditingRule(null);
            
            const ruleRes = await ReturnRuleServ.getAllRules();
            setAllRules(ruleRes?.data?.data || ruleRes?.data || ruleRes || []);
        } catch (error) {
            message.error(error.error || "Failed to save rule");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await ReturnRuleServ.deleteRule(id);
            message.success("Rule removed");
            const ruleRes = await ReturnRuleServ.getAllRules();
            setAllRules(ruleRes?.data?.data || ruleRes?.data || ruleRes || []);
        } catch (error) {
            message.error("Failed to delete rule");
        }
    };

    const getRuleDisplay = rule => {
        if (rule.type === "global") return "Global (All Products)";
        if (rule.type === "category") return `${rule.categoryId?.name || "Removed Category"} Category`;
        if (rule.type === "shop") return `${rule.shopId?.name || "Removed Shop"} Shop`;
        return "Unknown";
    };

    return (
        <div style={{ background: "linear-gradient(135deg,#f8f4ff,#fff7ed,#f0f9ff)", minHeight: "100vh", padding: 24, fontFamily: "'Instacart Sans',Arial,sans-serif" }}>
            <div style={{ marginBottom: 20 }}><Breadcrumbs /></div>

            <Spin spinning={pageLoading}>
                <div style={card} ref={formCardRef}>
                    <div style={editingRule ? {
                        background: "linear-gradient(135deg, #92400e 0%, #b45309 100%)",
                        padding: "18px 24px", display: "flex", alignItems: "center", gap: 12,
                    } : cardHeaderPurple}>
                        <div style={headerIcon}><SyncOutlined /></div>
                        <div>
                            <p style={headerTitle}>
                                {editingRule ? `Editing: ${getRuleDisplay(editingRule)}` : "Return Rules Engine"}
                            </p>
                            <p style={headerSub}>
                                {editingRule ? "Update the return eligibility below and click Save Rule" : "Configure return eligibility hierarchically across Global, Category, and Shop levels."}
                            </p>
                        </div>
                        {editingRule && (
                            <button
                                onClick={() => {
                                    setEditingRule(null);
                                    setTargetType("global");
                                    setSelectedParentId(null); setSelectedSubId(null); setSelectedShopId(null);
                                    form.setFieldsValue({ allowReturn: false });
                                }}
                                style={{
                                    marginLeft: "auto", background: "rgba(255,255,255,0.18)",
                                    border: "1.5px solid rgba(255,255,255,0.35)",
                                    borderRadius: 8, color: "#fff", padding: "4px 12px",
                                    fontSize: 12, fontWeight: 600, cursor: "pointer",
                                    display: "flex", alignItems: "center", gap: 6,
                                }}
                            >
                                <CloseOutlined /> Cancel Edit
                            </button>
                        )}
                    </div>

                    <div style={{ padding: 24 }}>
                        <Form form={form} layout="vertical" onFinish={handleSave} initialValues={{ allowReturn: false }}>
                            <div style={{
                                display: "grid",
                                gridTemplateColumns:
                                    targetType === "global" ? "1fr 1fr" :
                                        targetType === "shop" ? "1fr 1fr 1fr" :
                                            "1fr 1fr 1fr 1fr",
                                gap: 16,
                                alignItems: "start",
                            }}>
                                
                                <div>
                                    <span style={label}>Configuration Level</span>
                                    <Select value={targetType} size="large" style={{ width: "100%" }}
                                        onChange={val => { setTargetType(val); setSelectedParentId(null); setSelectedSubId(null); setSelectedShopId(null); }}>
                                        {Object.entries(TYPE_META).map(([k, m]) => (
                                            <Option key={k} value={k}>
                                                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                    <span style={{ color: m.color }}>{m.icon}</span>{m.label}
                                                </span>
                                            </Option>
                                        ))}
                                    </Select>
                                </div>

                                {targetType === "category" && (
                                    <div>
                                        <span style={label}><span style={req}>*</span>Parent Category</span>
                                        <Select placeholder="Select parent category" value={selectedParentId}
                                            onChange={onParentChange} showSearch optionFilterProp="children"
                                            size="large" style={{ width: "100%" }} allowClear>
                                            {parentCategories.map(c => <Option key={c._id} value={c._id}>{c.name}</Option>)}
                                        </Select>
                                    </div>
                                )}

                                {targetType === "category" && (
                                    <div>
                                        <span style={label}>Sub-Category <span style={{ color: "#aaa", fontWeight: 400 }}>(optional)</span></span>
                                        <Select placeholder="All sub-categories" value={selectedSubId} onChange={setSelectedSubId}
                                            allowClear disabled={!selectedParentId}
                                            showSearch optionFilterProp="children" size="large" style={{ width: "100%" }}>
                                            {subCategories.map(c => <Option key={c._id} value={c._id}>{c.name}</Option>)}
                                        </Select>
                                    </div>
                                )}

                                {targetType === "shop" && (
                                    <div>
                                        <span style={label}><span style={req}>*</span>Select Shop</span>
                                        <Select placeholder="Select a shop" value={selectedShopId} onChange={setSelectedShopId}
                                            showSearch optionFilterProp="children" size="large" style={{ width: "100%" }} allowClear>
                                            {shops.map(s => <Option key={s._id} value={s._id}>{s.name || s.email}</Option>)}
                                        </Select>
                                    </div>
                                )}

                                <div>
                                    <span style={label}><span style={req}>*</span>Return Eligibility</span>
                                    <div style={{ height: 40, display: "flex", alignItems: "center" }}>
                                        <Form.Item name="allowReturn" valuePropName="checked" style={{ margin: 0 }}>
                                            <Switch 
                                                checkedChildren="RETURNABLE" 
                                                unCheckedChildren="NON-RETURNABLE" 
                                                style={{ background: allowReturn ? '#16a34a' : '#dc2626' }} 
                                            />
                                        </Form.Item>
                                        <span style={{ marginLeft: 12, fontSize: 13, fontWeight: 600, color: allowReturn ? '#16a34a' : '#dc2626' }}>
                                            {allowReturn ? "Allowed" : "Not Allowed"}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div style={{
                                marginTop: 24, padding: "16px 20px", borderRadius: 12,
                                background: allowReturn ? 'rgba(22, 163, 74, 0.05)' : 'rgba(220, 38, 38, 0.05)',
                                border: `1.5px dashed ${allowReturn ? 'rgba(22, 163, 74, 0.3)' : 'rgba(220, 38, 38, 0.3)'}`,
                                display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                    <CheckCircleFilled style={{ color: allowReturn ? '#16a34a' : '#dc2626', fontSize: 20 }} />
                                    <div>
                                        <div style={{ fontSize: 11, fontWeight: 800, color: allowReturn ? '#15803d' : '#b91c1c', letterSpacing: 0.5, textTransform: "uppercase" }}>
                                            LIVE PREVIEW
                                        </div>
                                        <div style={{ fontSize: 15, fontWeight: 700, color: "#1e293b", marginTop: 2 }}>
                                            {allowReturn ? "Products in this scope can be returned by the customer" : "Products in this scope CANNOT be returned by the customer"}
                                        </div>
                                    </div>
                                </div>
                                <Button
                                    type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}
                                    style={{
                                        background: "linear-gradient(135deg, #FF8110 0%, #e66a00 100%)",
                                        border: "none", borderRadius: 8, padding: "0 24px", height: 42,
                                        fontWeight: 700, boxShadow: "0 4px 14px rgba(255,129,16,0.3)"
                                    }}
                                >
                                    Save Rule
                                </Button>
                            </div>
                        </Form>
                    </div>
                </div>

                <div style={card}>
                    <div style={cardHeaderGreen}>
                        <div style={{ ...headerIcon, color: "#fff", background: "rgba(255,255,255,0.2)" }}><CheckCircleFilled /></div>
                        <div>
                            <p style={headerTitle}>Active Configurations</p>
                            <p style={headerSub}>{allRules.length} rules configured</p>
                        </div>
                    </div>

                    {allRules.length === 0 ? (
                        <div style={{ padding: 40 }}>
                            <Empty description="No return rules configured yet. The system will default to ALLOW RETURNS for everything." />
                        </div>
                    ) : (
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr style={{ background: "linear-gradient(135deg,#f5f0ff,#fff0e6)" }}>
                                    <th style={{ ...thCell, width: 50, textAlign: "center" }}>#</th>
                                    <th style={thCell}>Scope Name</th>
                                    <th style={{ ...thCell, textAlign: "center" }}>Level</th>
                                    <th style={{ ...thCell, textAlign: "center" }}>Eligibility</th>
                                    <th style={{ ...thCell, textAlign: "center" }}>Priority</th>
                                    <th style={{ ...thCell, textAlign: "center" }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allRules.map((rule, idx) => {
                                    const meta = TYPE_META[rule.type] || TYPE_META.global;
                                    const pColor = PRIORITY_COLOR[rule.type] || "#888";
                                    const isH = hoveredRow === rule._id;

                                    return (
                                        <tr key={rule._id}
                                            style={{ background: isH ? `${meta.color}08` : "#fff", transition: "background 0.18s" }}
                                            onMouseEnter={() => setHoveredRow(rule._id)}
                                            onMouseLeave={() => setHoveredRow(null)}>
                                            <td style={{ ...tdCell, textAlign: "center", color: "#bbb", fontWeight: 700 }}>{idx + 1}</td>
                                            <td style={tdCell}>
                                                <span 
                                                    style={{ fontWeight: 700, fontSize: 14, color: '#0B1F4D', cursor: 'pointer', transition: 'all 0.2s' }}
                                                    onMouseEnter={e => e.currentTarget.style.color = '#FF8110'}
                                                    onMouseLeave={e => e.currentTarget.style.color = '#0B1F4D'}
                                                    onClick={() => {
                                                        setEditingRule(rule);
                                                        setTargetType(rule.type);
                                                        if (rule.type === 'category') {
                                                            const cat = categories.find(c => c._id === rule.categoryId?._id);
                                                            if (cat && cat.parentId) {
                                                                setSelectedParentId(cat.parentId);
                                                                setSelectedSubId(cat._id);
                                                                setSubCategories(categories.filter(c => c.parentId === cat.parentId));
                                                            } else {
                                                                setSelectedParentId(rule.categoryId?._id);
                                                                setSelectedSubId(null);
                                                                setSubCategories(categories.filter(c => c.parentId === rule.categoryId?._id));
                                                            }
                                                        } else if (rule.type === 'shop') {
                                                            setSelectedShopId(rule.shopId?._id);
                                                        }
                                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                                    }}
                                                >
                                                    {getRuleDisplay(rule)}
                                                </span>
                                            </td>
                                            <td style={{ ...tdCell, textAlign: "center" }}>
                                                <span style={pill(meta.tagColor, meta.tagBg)}>
                                                    {meta.icon}&nbsp;{rule.type.charAt(0).toUpperCase() + rule.type.slice(1)}
                                                </span>
                                            </td>
                                            <td style={{ ...tdCell, textAlign: "center" }}>
                                                <span style={{
                                                    padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 800, textTransform: 'uppercase',
                                                    background: rule.allowReturn ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.08)',
                                                    color: rule.allowReturn ? '#16a34a' : '#dc2626',
                                                }}>
                                                    {rule.allowReturn ? "RETURNABLE" : "NON-RETURNABLE"}
                                                </span>
                                            </td>
                                            <td style={{ ...tdCell, textAlign: "center" }}>
                                                <span style={pill(pColor, `${pColor}15`)}>{PRIORITY_LABEL[rule.type]}</span>
                                            </td>
                                            <td style={{ ...tdCell, textAlign: "center" }}>
                                                <Popconfirm title="Remove this rule?" onConfirm={() => handleDelete(rule._id)}>
                                                    <Button type="primary" danger shape="circle" icon={<DeleteOutlined />} size="small" />
                                                </Popconfirm>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </Spin>
        </div>
    );
};

export default ReturnControl;
